import { Badge } from '@/components/ui/badge';
import { Car } from 'lucide-react';
import { cn } from '@/lib/utils';
import { memo } from 'react';
import { useDroppable, useDraggable } from '@dnd-kit/core';

interface ParkingSpot {
  id: string;
  label: string;
  spot_type: string;
  position_x: number;
  position_y: number;
  width: number;
  height: number;
  is_active: boolean;
}

interface BirdseyeParkingSpotCardProps {
  spot: ParkingSpot;
  ticket: any | null; // ticket parked in this spot
  onClick?: () => void;
  readOnly?: boolean;
}

// Draggable ticket content for occupied spots
const DraggableParkingTicket = memo(({ ticket, readOnly }: { ticket: any; readOnly?: boolean }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `ticket:${ticket.id}`,
    disabled: readOnly,
  });

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className={cn(
        "p-2 bg-primary/10 rounded border border-primary/20 text-xs space-y-1 transition-all",
        !readOnly && "cursor-grab active:cursor-grabbing",
        isDragging && "opacity-50"
      )}
    >
      <p className="font-semibold truncate">
        {ticket.vehicle?.year} {ticket.vehicle?.make} {ticket.vehicle?.model}
      </p>
      <p className="text-muted-foreground text-[10px] truncate">{ticket.customerName || ticket.customer_name}</p>
      {ticket.ro_number && (
        <p className="text-muted-foreground text-[10px]">RO #{ticket.ro_number}</p>
      )}
      {/*
        Surface the dispatch plan when a parked vehicle has a planned tech.
        The `scheduled_technician` join is populated by Dashboard's tickets
        query (and would be by Birdseye's tickets fetch); we render it
        defensively under multiple shapes so any caller that already has
        a tech name keyed differently still works.
      */}
      {(ticket.scheduled_technician?.name ||
        ticket.scheduled_tech_name) && (
        <p className="text-muted-foreground text-[10px] truncate">
          Scheduled: {ticket.scheduled_technician?.name ?? ticket.scheduled_tech_name}
        </p>
      )}
      <Badge
        variant="secondary"
        className="text-[10px] mt-1"
      >
        {ticket.status}
      </Badge>
    </div>
  );
});

DraggableParkingTicket.displayName = 'DraggableParkingTicket';

export const BirdseyeParkingSpotCard = memo(({
  spot,
  ticket,
  onClick,
  readOnly,
}: BirdseyeParkingSpotCardProps) => {
  const isOccupied = !!ticket;

  // Make spot a droppable zone
  const { isOver, setNodeRef } = useDroppable({
    id: `spot:${spot.id}`,
  });

  return (
    <div
      ref={setNodeRef}
      data-testid={`birdseye-spot-${spot.id}`}
      className={cn(
        'absolute rounded-lg border-2 border-dashed transition-all cursor-pointer hover:shadow-md',
        isOccupied ? 'bg-card border-accent' : 'bg-muted/30 border-muted-foreground/30',
        isOver && 'ring-2 ring-blue-500 ring-offset-2 ring-offset-background bg-blue-500/5 border-blue-500',
      )}
      style={{
        left: `${spot.position_x}px`,
        top: `${spot.position_y}px`,
        width: `${spot.width}px`,
        height: `${spot.height}px`,
      }}
      onClick={onClick}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <Car className="h-4 w-4 shrink-0 text-muted-foreground" />
          <h3 className="font-bold text-sm truncate">{spot.label}</h3>
        </div>
        <Badge variant="outline" className="text-[10px] shrink-0 border-muted-foreground/40 text-muted-foreground">
          Parking
        </Badge>
      </div>

      {/* Parked vehicle (draggable) or empty */}
      <div className="px-2 pb-2">
        {ticket ? (
          <DraggableParkingTicket ticket={ticket} readOnly={readOnly} />
        ) : (
          <div className="p-2 bg-muted/20 rounded text-xs text-center text-muted-foreground">
            Empty
          </div>
        )}
      </div>
    </div>
  );
});

BirdseyeParkingSpotCard.displayName = 'BirdseyeParkingSpotCard';
